import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Search, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Filter, X, Database } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
  "Assigned": "bg-[#EFF5ED] text-[#4A7A56]",
  "In Field": "bg-blue-50 text-blue-700",
  "Hold": "bg-amber-50 text-amber-700",
  "Returned": "bg-red-50 text-red-700",
  "Completed": "bg-green-50 text-green-700",
  "Archived": "bg-gray-100 text-gray-500",
  "Available": "bg-purple-50 text-purple-700",
};

const AllCompanyOrdersPage = ({ Layout, department }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [data, setData] = useState({ orders: [], total: 0, page: 1, pages: 0, filters: { companies: [], inspectors: [], statuses: [] } });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");
  // Auto-populate search from `?q=...` query param. Used by the Inspector
  // Validate Route report to deep-link a missing address into the lookup.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("q");
    if (q) {
      setSearchInput(q);
      setSearch(q);
      setPage(1);
    }
  }, [location.search]);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({ status: "", company: "", inspector: "", archived: "", paid: "", active: "" });
  const [sortBy, setSortBy] = useState("updated_at");
  const [sortDir, setSortDir] = useState("desc");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 100, search: search || undefined };
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/orders/all-company`, { params });
      setData(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [page, search, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSearch = (e) => { e.preventDefault(); setSearch(searchInput); setPage(1); };
  const handleFilter = (key, value) => { setFilters(prev => ({ ...prev, [key]: value })); setPage(1); };
  const clearFilters = () => { setFilters({ status: "", company: "", inspector: "", archived: "", paid: "", active: "" }); setSearch(""); setSearchInput(""); setPage(1); };

  const activeFilterCount = Object.values(filters).filter(Boolean).length + (search ? 1 : 0);

  // Client-side sort
  const sortedOrders = [...(data.orders || [])].sort((a, b) => {
    let aV = a[sortBy] || "", bV = b[sortBy] || "";
    return sortDir === "asc" ? (aV < bV ? -1 : aV > bV ? 1 : 0) : (aV > bV ? -1 : aV < bV ? 1 : 0);
  });

  const handleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortBy(key); setSortDir("desc"); }
  };

  const columns = [
    { key: "order_number", label: "Order #", w: "100px" },
    { key: "company", label: "Company", w: "100px" },
    { key: "order_type", label: "Type", w: "70px" },
    { key: "flow_type", label: "Flow", w: "70px" },
    { key: "status", label: "Status", w: "90px" },
    { key: "inspector", label: "Inspector", w: "120px" },
    { key: "address", label: "Address", w: "180px" },
    { key: "city", label: "City", w: "100px" },
    { key: "state", label: "ST", w: "40px" },
    { key: "due_date", label: "Due Date", w: "85px" },
    { key: "transmitted", label: "Trans", w: "50px" },
    { key: "available", label: "Avail", w: "50px" },
    { key: "active", label: "Active", w: "50px" },
    { key: "archived", label: "Arch", w: "50px" },
    { key: "paid", label: "Paid", w: "50px" },
    { key: "updated_at", label: "Updated", w: "90px" },
  ];

  return (
    <Layout>
      <div className="space-y-4" data-testid="all-company-orders-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(-1)} className="p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600" data-testid="back-btn">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-[#1F2937] flex items-center gap-2">
                <Database className="h-5 w-5 text-[#2F5D8A]" />
                All Company Orders
              </h1>
              <p className="text-xs text-gray-400 mt-0.5">{department} — {data.total.toLocaleString()} total orders in company history</p>
            </div>
          </div>
        </div>

        {/* Search + Filter bar */}
        <div className="flex items-center gap-2">
          <form onSubmit={handleSearch} className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
            <input type="text" value={searchInput} onChange={e => setSearchInput(e.target.value)} placeholder="Search order #, address, city..."
              className="w-full pl-9 pr-3 py-2 bg-white border border-gray-200 rounded-lg text-sm" data-testid="search-input" />
          </form>
          <button onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border transition-colors ${activeFilterCount > 0 ? 'bg-[#2F5D8A] text-white border-[#2F5D8A]' : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'}`}
            data-testid="filter-toggle">
            <Filter className="h-3.5 w-3.5" />
            Filters {activeFilterCount > 0 && `(${activeFilterCount})`}
          </button>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="flex items-center gap-1 px-2 py-2 text-xs text-red-500 hover:bg-red-50 rounded-lg" data-testid="clear-filters">
              <X className="h-3 w-3" /> Clear
            </button>
          )}
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="bg-white rounded-lg border border-gray-200 p-4 grid grid-cols-3 md:grid-cols-6 gap-3" data-testid="filter-panel">
            <div>
              <label className="text-[10px] text-gray-500 font-medium">Status</label>
              <select value={filters.status} onChange={e => handleFilter("status", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" data-testid="filter-status">
                <option value="">All</option>
                {data.filters.statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-medium">Company</label>
              <select value={filters.company} onChange={e => handleFilter("company", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" data-testid="filter-company">
                <option value="">All</option>
                {data.filters.companies.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-medium">Inspector</label>
              <select value={filters.inspector} onChange={e => handleFilter("inspector", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" data-testid="filter-inspector">
                <option value="">All</option>
                {data.filters.inspectors.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-medium">Archived</label>
              <select value={filters.archived} onChange={e => handleFilter("archived", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" data-testid="filter-archived">
                <option value="">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-medium">Paid</label>
              <select value={filters.paid} onChange={e => handleFilter("paid", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" data-testid="filter-paid">
                <option value="">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
            <div>
              <label className="text-[10px] text-gray-500 font-medium">Active</label>
              <select value={filters.active} onChange={e => handleFilter("active", e.target.value)} className="w-full mt-0.5 px-2 py-1.5 border border-gray-200 rounded text-xs bg-white" data-testid="filter-active">
                <option value="">All</option>
                <option value="yes">Yes</option>
                <option value="no">No</option>
              </select>
            </div>
          </div>
        )}

        {/* Table */}
        {loading ? (
          <div className="flex items-center justify-center py-20"><div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#2F5D8A]" /></div>
        ) : sortedOrders.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 py-12 text-center text-sm text-gray-400" data-testid="no-results">No orders found</div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="orders-table">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50/50">
                    {columns.map(col => (
                      <th key={col.key} className="px-2 py-2 text-left text-[10px] font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:text-gray-700 whitespace-nowrap"
                        style={{ minWidth: col.w }} onClick={() => handleSort(col.key)}>
                        <div className="flex items-center gap-0.5">
                          {col.label}
                          {sortBy === col.key && (sortDir === "asc" ? <ChevronUp className="h-2.5 w-2.5" /> : <ChevronDown className="h-2.5 w-2.5" />)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {sortedOrders.map(o => (
                    <tr key={o.id} className="hover:bg-gray-50/50 cursor-pointer transition-colors" onClick={() => navigate(`/order/${o.id}`)} data-testid={`order-row-${o.order_number}`}>
                      <td className="px-2 py-2 font-medium text-[#2F5D8A] whitespace-nowrap">{o.order_number}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{o.company}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{o.order_type}</td>
                      <td className="px-2 py-2"><span className={`px-1 py-0.5 rounded text-[8px] font-bold ${o.flow_type === "Internal" ? "bg-blue-50 text-blue-700" : o.flow_type === "External" ? "bg-orange-50 text-orange-700" : "bg-gray-50 text-gray-600"}`}>{o.flow_type || "-"}</span></td>
                      <td className="px-2 py-2"><span className={`px-1.5 py-0.5 rounded text-[8px] font-bold whitespace-nowrap ${STATUS_COLORS[o.status] || "bg-gray-100 text-gray-500"}`}>{o.status}</span></td>
                      <td className="px-2 py-2 whitespace-nowrap truncate max-w-[120px]" title={o.inspector}>{o.inspector || "-"}</td>
                      <td className="px-2 py-2 whitespace-nowrap truncate max-w-[180px]" title={o.address}>{o.address || "-"}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{o.city}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{o.state}</td>
                      <td className="px-2 py-2 whitespace-nowrap">{o.due_date || "-"}</td>
                      <td className="px-2 py-2 text-center"><span className={`text-[8px] font-bold ${o.transmitted === "Yes" ? "text-green-600" : "text-gray-400"}`}>{o.transmitted}</span></td>
                      <td className="px-2 py-2 text-center"><span className={`text-[8px] font-bold ${o.available === "Yes" ? "text-green-600" : "text-gray-400"}`}>{o.available}</span></td>
                      <td className="px-2 py-2 text-center"><span className={`text-[8px] font-bold ${o.active === "Yes" ? "text-green-600" : "text-gray-400"}`}>{o.active}</span></td>
                      <td className="px-2 py-2 text-center"><span className={`text-[8px] font-bold ${o.archived === "Yes" ? "text-amber-600" : "text-gray-400"}`}>{o.archived}</span></td>
                      <td className="px-2 py-2 text-center"><span className={`text-[8px] font-bold ${o.paid === "Yes" ? "text-[#4A7A56]" : "text-gray-400"}`}>{o.paid}</span></td>
                      <td className="px-2 py-2 whitespace-nowrap text-gray-400">{o.updated_at ? o.updated_at.slice(0, 10) : "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {data.pages > 1 && (
              <div className="flex items-center justify-between px-4 py-2 border-t border-gray-200">
                <span className="text-xs text-gray-500">Page {data.page} of {data.pages} ({data.total} orders)</span>
                <div className="flex gap-1">
                  <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="px-2 py-1 text-xs bg-gray-100 rounded disabled:opacity-30 hover:bg-gray-200" data-testid="prev-page"><ChevronLeft className="h-3 w-3" /></button>
                  <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)} className="px-2 py-1 text-xs bg-gray-100 rounded disabled:opacity-30 hover:bg-gray-200" data-testid="next-page"><ChevronRight className="h-3 w-3" /></button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AllCompanyOrdersPage;
